import { use, useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useUrl } from 'crossroad';
import { PassageDataContext } from '@/data/passage/passage-data-context';
import { LiteraryFeaturesDataContext } from '@/data/literary-features/literary-feature-data-context';
import { useLiteraryFeaturesByPassageData } from '@/data/literary-features/literary-features-by-passage-data-context';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { SourceLinks } from '@/components/ui/source-links';
import PassageGenreGuide from '@/components/passage/passage-genre-guide';
import { LiteraryFeatureCrossReferenceModal } from '@/components/literary-features/LiteraryFeatureCrossReferenceModal';
import { formatChiasmPattern, formatParallelismPattern, formatInclusioPattern, formatTricolonPattern, formatMerismPattern } from './literary-context-utils';
import {
    ChevronUp,
    ChevronDown,
    Scroll,
    ArrowRight,
    BookOpen
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { AIDisclaimerBadge } from '../ui/ai-disclaimer-badge';
import { cn } from '@/lib/utils';
import structureIllustration from '@/assets/structure-background.png';
import toolsIllustration from '@/assets/literary-tools-background.png';
import poeticIllustration from '@/assets/poetic-background.png';
import oralIllustration from '@/assets/oral-tradition-background.png';
import { MicroStructureCard } from './micro-structure-card';
import { LiteraryDeviceCard } from './literary-device-card';
import { ImageryCard } from './imagery-card';
import { PoeticFeatureCard } from './poetic-feature-card';
import { WordplayCard } from './wordplay-card';
import { NarrativeTechniqueCard } from './narrative-technique-card';
import { OralTraditionCard } from './oral-tradition-card';
import { AuthorVoiceCard } from './author-voice-card';
import { KeyVerseCandidatesCard } from './key-verse-candidates-card';
import { MacroStructureOverviewCard } from './macro-structure-overview-card';
import { PivotsAndHingesCard } from './pivots-and-hinges-card';
import { SeamsAndFramesCard } from './seams-and-frames-card';
import { RepeatedPatternsCard } from './repeated-patterns-card';
import { IntertextualArcsCard } from './intertextual-arcs-card';
// Hero illustration
import structureHero from '@/assets/heroes/structure_background_v3.png';
import { ContextHero } from '../context/ContextHero';
import { normalizeBookName } from '@/lib/utils/bible-utils';
import { getGenreInfo } from '@/lib/utils/genre-utils';

interface Props {
    passageId: string;
}

function LoadingSkeleton() {
    return (
        <div className="space-y-4 p-4">
            <Card>
                <CardHeader>
                    <Skeleton className="h-6 w-48" />
                </CardHeader>
                <CardContent className="px-4">
                    <Skeleton className="h-4 w-full mb-2" />
                    <Skeleton className="h-4 w-3/4" />
                </CardContent>
            </Card>
        </div>
    );
}

function EmptyState() {
    return (
        <div className="space-y-4 p-4">
            <Card>
                <CardContent className="pt-6 px-4">
                    <p className="text-sm text-muted-foreground text-center">
                        No passage data available.
                    </p>
                </CardContent>
            </Card>
        </div>
    );
}

export default function PassageLiteraryContext({ passageId }: Props) {
    const passageContext = use(PassageDataContext);
    const literaryFeaturesContext = use(LiteraryFeaturesDataContext);
    const { data: passageFeatures, isLoading: featuresLoading } = useLiteraryFeaturesByPassageData(passageId);
    const [url, setUrl] = useUrl();
    const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set([
        'structural-role',
        'literary-devices',
        'rhetorical-features-devices',
        'wordplay',
        'imagery-passage',
        'narrative-techniques',
        'poetic-features'
    ]));
    const [isBookLevelVisible, setIsBookLevelVisible] = useState(false);
    const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set([
        'macro-structure',
        'pivots-hinges',
        'seams-frames',
        'key-verses',
        'repeated-patterns',
        'intertextual-arcs',
        'author-voice',
        'oral-tradition'
    ]));
    const [isSummaryExpanded, setIsSummaryExpanded] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const summaryRef = useRef<HTMLParagraphElement>(null);
    const hasAutoExpanded = useRef(false);

    useEffect(() => {
        const checkOverflow = () => {
            if (summaryRef.current) {
                if (!isSummaryExpanded) {
                    setIsOverflowing(summaryRef.current.scrollHeight > summaryRef.current.offsetHeight);
                }
            }
        };

        checkOverflow();
        window.addEventListener('resize', checkOverflow);
        return () => window.removeEventListener('resize', checkOverflow);
    }, [isSummaryExpanded, featuresLoading, passageFeatures?.structuralRole]);

    const toggleItemExpansion = useCallback((itemId: string) => {
        setExpandedItems(prev => {
            const next = new Set(prev);
            if (next.has(itemId)) next.delete(itemId);
            else next.add(itemId);
            return next;
        });
    }, []);

    useEffect(() => {
        if (!featuresLoading && passageFeatures?.microStructures && !hasAutoExpanded.current) {
            setExpandedSections(prev => {
                const next = new Set(prev);
                passageFeatures.microStructures.forEach((_, index) => {
                    next.add(`microStructure-${index}`);
                });
                return next;
            });
            hasAutoExpanded.current = true;
        }
    }, [featuresLoading, passageFeatures?.microStructures]);

    // State for cross-reference modal
    const [crossRefModal, setCrossRefModal] = useState<{
        type: 'literaryDevice' | 'rhetoricalFeature' | 'theme' | 'subgenre' | 'genre' | 'poeticFeature' | 'imagery' | 'narrativeTechnique' | 'microStructure' | 'bookRhetoricalDevice' | 'form';
        value: string;
    } | null>(null);

    const passage = useMemo(() => {
        return passageContext.data?.items.find(p => p.id === passageId);
    }, [passageContext.data, passageId]);

    const genreInfo = useMemo(() => {
        if (!passage?.genre || !passage?.subgenre || !passage?.form) return null;
        return getGenreInfo(passage.genre, passage.subgenre, passage.form);
    }, [passage]);

    const summary = passageFeatures?.structuralRole || genreInfo?.short_description;

    const { previousSection, nextSection } = useMemo(() => {
        if (!passageContext.data?.items) {
            return { previousSection: null, nextSection: null };
        }

        const currentIndex = passageContext.data.items.findIndex(s => s.id === passageId);
        if (currentIndex === -1) {
            return { previousSection: null, nextSection: null };
        }

        const previousSection = currentIndex > 0 ? passageContext.data.items[currentIndex - 1] : null;
        const nextSection = currentIndex < passageContext.data.items.length - 1 ? passageContext.data.items[currentIndex + 1] : null;

        return { previousSection, nextSection };
    }, [passageContext.data?.items, passageId]);

    const bookFeatures = useMemo(() => {
        if (!literaryFeaturesContext || !passage) {
            return null;
        }
        return literaryFeaturesContext.getLiteraryFeaturesByBook(passage.book);
    }, [literaryFeaturesContext, passage]);

    const toggleSection = useCallback((section: string) => {
        setExpandedSections(prev => {
            const next = new Set(prev);
            if (next.has(section)) {
                next.delete(section);
            } else {
                next.add(section);
            }
            return next;
        });
    }, []);

    const handlePrevious = useCallback(() => {
        if (previousSection) {
            const newUrl = url.path.replace(`/${passageId}`, `/${previousSection.id}`);
            setUrl(newUrl);
        }
    }, [previousSection, setUrl, url.path, passageId]);

    const handleNext = useCallback(() => {
        if (nextSection) {
            const newUrl = url.path.replace(`/${passageId}`, `/${nextSection.id}`);
            setUrl(newUrl);
        }
    }, [nextSection, setUrl, url.path, passageId]);

    // Pre-calculate structure formatting to avoid recalculating on every render
    const formattedMicroStructures = useMemo(() => {
        if (!passageFeatures?.microStructures) return [];

        return passageFeatures.microStructures.map(structure => {
            const typeLower = structure.type.toLowerCase();
            const isChiasm = typeLower === 'chiasm';
            const isParallelism = typeLower.includes('parallelism') && typeLower !== 'chiasm';
            const isInclusio = typeLower === 'inclusio';
            const isTricolon = typeLower === 'tricolon';
            const isMerism = typeLower === 'merism';

            return {
                ...structure,
                isChiasm,
                isParallelism,
                isInclusio,
                isTricolon,
                isMerism,
                chiasmElements: isChiasm ? formatChiasmPattern(structure.pattern) : [],
                parallelismElements: isParallelism ? formatParallelismPattern(structure.pattern, structure.type) : [],
                inclusioElements: isInclusio ? formatInclusioPattern(structure.pattern) : null,
                tricolonElements: isTricolon ? formatTricolonPattern(structure.pattern) : [],
                merismElements: isMerism ? formatMerismPattern(structure.pattern) : []
            };
        });
    }, [passageFeatures?.microStructures]);

    const onSubgenreClick = useCallback((subgenre: string) => {
        setCrossRefModal({ type: 'subgenre', value: subgenre });
    }, []);

    if (featuresLoading || !passageContext.data || !literaryFeaturesContext) {
        return <LoadingSkeleton />;
    }

    if (!passage || !passageFeatures) {
        return <EmptyState />;
    }

    return (
        <div className="space-y-8">
            <ContextHero
                eyebrow="LITERARY CONTEXT"
                headline="Literary Artistry"
                subtitle="Identify the intentional patterns, genres, and structures used to weave the message of the text."
                image={structureHero}
                onClose={() => setUrl(`/app/passage/${passageId}`)}
                showClose={false}
                showHandle={false}
                lockBackground={true}
                backgroundOffset="center -300px"
                className=""
            >
                <PassageGenreGuide
                    passage={passage}
                    structuralRole={passageFeatures?.structuralRole}
                    onGenreClick={(genre) => setCrossRefModal({ type: 'genre', value: genre })}
                    onSubgenreClick={onSubgenreClick}
                    onFormClick={(form) => setCrossRefModal({ type: 'form', value: form })}
                    mode="hero"
                />
            </ContextHero>

            <div className="space-y-12 px-4">
                {/* Summary / Structural Role */}
                {summary && (
                    <div className="mb-4 relative group">
                        <p
                            ref={summaryRef}
                            className={cn(
                                "text-lg md:text-xl leading-relaxed text-foreground/80 italic border-l-2 border-primary/20 pl-6 transition-all duration-300",
                                !isSummaryExpanded && "line-clamp-3"
                            )}
                        >
                            {summary}
                        </p>
                        {(isOverflowing || isSummaryExpanded) && (
                            <button
                                onClick={() => setIsSummaryExpanded(!isSummaryExpanded)}
                                className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary/60 hover:text-primary transition-colors mt-2 ml-6 flex items-center gap-1"
                            >
                                {isSummaryExpanded ? (
                                    <>Show Less <ChevronUp className="h-3 w-3" /></>
                                ) : (
                                    <>Show More <ChevronDown className="h-3 w-3" /></>
                                )}
                            </button>
                        )}
                    </div>
                )}

                {/* ========== PASSAGE-SPECIFIC LITERARY FEATURES ========== */}

                {/* Structure */}
                {(passageFeatures?.structuralRole || passageFeatures?.microStructures?.length > 0 || (passageFeatures?.connections && (passageFeatures.connections.previous || passageFeatures.connections.next))) && (
                    <div className="space-y-6">
                        <div className="space-y-1">
                            <h2 className="text-2xl font-serif font-semibold tracking-tight text-foreground/90">
                                Structure & Organization
                            </h2>
                            <div className="flex items-center gap-2">
                                <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-primary/60">
                                    Framework & Connections
                                </span>
                                <div className="h-px w-8 bg-primary/20" />
                            </div>
                        </div>

                        <div className="mt-6 space-y-4">
                            {formattedMicroStructures.map((structure, index) => {
                                const itemId = `microStructure-${index}`;
                                return (
                                    <MicroStructureCard
                                        key={index}
                                        structure={structure}
                                        isExpanded={expandedSections.has(itemId)}
                                        onToggleExpansion={() => toggleSection(itemId)}
                                        onStructureClick={(type) => setCrossRefModal({
                                            type: 'microStructure',
                                            value: type
                                        })}
                                    />
                                );
                            })}

                            {(passageFeatures?.connections && (passageFeatures.connections.previous || passageFeatures.connections.next)) && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
                                    {passageFeatures.connections.previous && (
                                        <div
                                            className={cn(
                                                "p-4 rounded-xl border border-border/40 bg-card/30 backdrop-blur-sm space-y-2 group transition-colors",
                                                previousSection ? "cursor-pointer hover:bg-primary/5" : "opacity-70"
                                            )}
                                            onClick={handlePrevious}
                                        >
                                            <div className={cn(
                                                "flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-primary/60 transition-colors",
                                                previousSection && "group-hover:text-primary"
                                            )}>
                                                <ChevronUp className="w-3 h-3 rotate-[-90deg]" />
                                                Prev Passage
                                            </div>
                                            <p className={cn(
                                                "text-sm text-foreground/80 font-serif font-medium leading-relaxed transition-colors",
                                                previousSection && "group-hover:text-primary"
                                            )}>
                                                {passageFeatures.connections.previous}
                                            </p>
                                        </div>
                                    )}
                                    {passageFeatures.connections.next && (
                                        <div
                                            className={cn(
                                                "p-4 rounded-xl border border-border/40 bg-card/30 backdrop-blur-sm space-y-2 text-right sm:text-left group transition-colors",
                                                nextSection ? "cursor-pointer hover:bg-primary/5" : "opacity-70"
                                            )}
                                            onClick={handleNext}
                                        >
                                            <div className={cn(
                                                "flex items-center justify-end sm:justify-start gap-2 text-[10px] font-bold uppercase tracking-widest text-primary/60 transition-colors",
                                                nextSection && "group-hover:text-primary"
                                            )}>
                                                Next Passage
                                                <ChevronUp className="w-3 h-3 rotate-[90deg]" />
                                            </div>
                                            <p className={cn(
                                                "text-sm text-foreground/80 font-serif font-medium leading-relaxed transition-colors",
                                                nextSection && "group-hover:text-primary"
                                            )}>
                                                {passageFeatures.connections.next}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                )}

                {/* Literary Devices & Wordplay */}
                {(passageFeatures?.literaryDevices?.length > 0 || passageFeatures?.wordplay?.length > 0) && (
                    <div className="mt-12 space-y-6">
                        <div className="space-y-1">
                            <h2 className="text-2xl font-serif font-semibold tracking-tight text-foreground/90">
                                Rhetorical & Linguistic Devices
                            </h2>
                            <div className="flex items-center gap-2">
                                <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-primary/60">
                                    Style & Wordplay
                                </span>
                                <div className="h-px w-8 bg-primary/20" />
                            </div>
                        </div>

                        <div className="mt-6 space-y-6">
                            {passageFeatures?.literaryDevices?.map((device, index) => {
                                const itemId = `device-${index}`;
                                return (
                                    <LiteraryDeviceCard
                                        key={index}
                                        device={device}
                                        onDeviceClick={(dev) => setCrossRefModal({
                                            type: 'literaryDevice',
                                            value: dev
                                        })}
                                    />
                                );
                            })}

                            {passageFeatures?.wordplay?.map((word, index) => {
                                const itemId = `wordplay-${index}`;
                                return (
                                    <WordplayCard
                                        key={index}
                                        wordplay={word}
                                    />
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Poetic Features & Imagery */}
                {
                    (passageFeatures?.poeticFeatures?.length > 0 || passageFeatures?.imagery?.length > 0) && (
                        <div className="mt-12 space-y-6">
                            <div className="space-y-1">
                                <h2 className="text-2xl font-serif font-semibold tracking-tight text-foreground/90">
                                    Poetic Patterns & Vision
                                </h2>
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-primary/60">
                                        Imagery & Rhythm
                                    </span>
                                    <div className="h-px w-8 bg-primary/20" />
                                </div>
                            </div>

                            <div className="mt-6 space-y-6">
                                {passageFeatures?.poeticFeatures?.map((feature, index) => {
                                    const itemId = `poetic-${index}`;
                                    return (
                                        <PoeticFeatureCard
                                            key={index}
                                            feature={feature}
                                        />
                                    );
                                })}

                                {passageFeatures?.imagery?.map((img, index) => {
                                    const itemId = `imagery-${index}`;
                                    return (
                                        <ImageryCard
                                            key={index}
                                            imagery={img}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    )}

                {/* Narrative Techniques & Oral Tradition */}
                {
                    (passageFeatures?.narrativeTechniques?.length > 0) && (
                        <div className="mt-12 space-y-6">
                            <div className="space-y-1">
                                <h2 className="text-2xl font-serif font-semibold tracking-tight text-foreground/90">
                                    Narrative Dynamics
                                </h2>
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-primary/60">
                                        Voice & Technique
                                    </span>
                                    <div className="h-px w-8 bg-primary/20" />
                                </div>
                            </div>

                            <div className="mt-6 space-y-6">
                                {passageFeatures?.narrativeTechniques?.map((technique, index) => {
                                    const itemId = `narrative-${index}`;
                                    return (
                                        <NarrativeTechniqueCard
                                            key={index}
                                            technique={technique}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    )}

                {/* Passage-Level Sources */}
                {
                    passageFeatures?.sources?.length > 0 && (
                        <div className="px-3 pb-4">
                            <SourceLinks sources={passageFeatures.sources} />
                        </div>
                    )
                }

                {/* ========== BOOK-LEVEL LITERARY CONTEXT DISCOVERY ========== */}
                {
                    bookFeatures && (
                        <div className="mt-4 relative">
                            {/* Cinematic Transition Button Variant */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                className={cn(
                                    "relative overflow-hidden p-8 md:p-12 rounded-[2.5rem] border border-border/40 bg-card/10 backdrop-blur-md transition-all duration-500 group/masterplan shadow-[0_0_50px_rgba(0,0,0,0.1)]",
                                )}
                            >
                                {/* Background Illustration */}
                                <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
                                    <div
                                        className="absolute inset-0 opacity-[0.4] saturate-[0.8] grayscale-[0.2] transition-transform duration-1000 group-hover/masterplan:scale-105"
                                        style={{
                                            backgroundImage: `url(${toolsIllustration})`,
                                            backgroundSize: 'cover',
                                            backgroundPosition: 'right center',
                                            maskImage: 'linear-gradient(to left, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 50%, transparent 100%)',
                                            WebkitMaskImage: 'linear-gradient(to left, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 50%, transparent 100%)'
                                        }}
                                    />
                                </div>

                                {/* Blueprint Subtle Grid Overlay */}
                                <div className="absolute inset-0 opacity-[0.03] pointer-events-none group-hover/masterplan:opacity-[0.05] transition-opacity" style={{ backgroundImage: 'linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

                                {/* Aesthetic BG elements */}
                                <div className="absolute top-0 right-0 p-8 opacity-5 z-10 pointer-events-none">
                                    <BookOpen className="w-32 h-32" />
                                </div>
                                <div className="absolute -left-12 -bottom-12 w-64 h-64 bg-primary/5 blur-[100px] rounded-full pointer-events-none" />

                                <div className="relative z-10 max-w-2xl space-y-6">
                                    <div className="space-y-4">
                                        <div className="flex items-center gap-3">
                                            <span className="text-[10px] font-bold uppercase tracking-[0.4em] text-primary/60">Book-Level Artistic Profile</span>
                                            <div className="h-px flex-1 bg-primary/10" />
                                        </div>
                                        <h3 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground leading-tight font-serif">
                                            The {passage?.book} Master Plan
                                        </h3>
                                    </div>

                                    <p className="text-base md:text-lg leading-relaxed text-muted-foreground/90 italic font-medium">
                                        The artistic voice of {normalizeBookName(passage.book)} provides the essential blueprint for understanding how this specific passage was shaped.
                                    </p>

                                    <div className="pt-4 flex flex-col sm:flex-row items-start sm:items-center gap-6">
                                        <Button
                                            size="lg"
                                            onClick={() => setIsBookLevelVisible(!isBookLevelVisible)}
                                            className="h-12 px-8 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold uppercase tracking-widest text-[11px] shadow-[0_0_20px_rgba(212,175,55,0.2)] hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] transition-all"
                                        >
                                            {isBookLevelVisible ? "Collapse Book Context" : `View ${passage?.book} Artistry`}
                                            <ArrowRight className={cn("ml-2 h-4 w-4 transition-transform", isBookLevelVisible && "rotate-90")} />
                                        </Button>

                                        <div className="flex items-center gap-2 text-[10px] font-bold text-muted-foreground/50 uppercase tracking-widest">
                                            <Scroll className="h-3.5 w-3.5" />
                                            <span>Architectural Overview</span>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>

                            <AnimatePresence>
                                {isBookLevelVisible && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: "auto" }}
                                        exit={{ opacity: 0, height: 0 }}
                                        transition={{ duration: 0.5, ease: "easeInOut" }}
                                        className="overflow-hidden"
                                    >
                                        <div className="space-y-6 pt-12">
                                            {/* Section 1: Structure & Architecture */}
                                            <div className="space-y-6">
                                                <div className="space-y-1">
                                                    <h2 className="text-2xl font-serif font-semibold tracking-tight text-foreground/90">
                                                        Structure & Architecture
                                                    </h2>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-primary/60">
                                                            The Framework
                                                        </span>
                                                        <div className="h-px w-8 bg-primary/20" />
                                                    </div>
                                                </div>

                                                {/* Macro Structure */}
                                                <MacroStructureOverviewCard
                                                    structures={bookFeatures.macroStructure}
                                                    isExpanded={expandedItems.has('macro-structure')}
                                                    onToggleExpansion={() => toggleItemExpansion('macro-structure')}
                                                    thumbnailUrl={structureIllustration}
                                                />

                                                {/* Pivots & Hinges */}
                                                <PivotsAndHingesCard
                                                    pivots={bookFeatures.pivotsAndHinges}
                                                    isExpanded={expandedItems.has('pivots-hinges')}
                                                    onToggleExpansion={() => toggleItemExpansion('pivots-hinges')}
                                                    onRefClick={(ref) => setUrl(`/${ref}`)}
                                                    thumbnailUrl={structureIllustration}
                                                />

                                                {/* Seams & Frames */}
                                                <SeamsAndFramesCard
                                                    seams={bookFeatures.seamsAndFrames}
                                                    isExpanded={expandedItems.has('seams-frames')}
                                                    onToggleExpansion={() => toggleItemExpansion('seams-frames')}
                                                    onRefClick={(ref) => setUrl(`/${ref}`)}
                                                    thumbnailUrl={structureIllustration}
                                                />
                                            </div>

                                            {/* Section 2: Compositional Mechanics */}
                                            <div className="space-y-6 pt-8">
                                                <div className="space-y-1">
                                                    <h2 className="text-2xl font-serif font-semibold tracking-tight text-foreground/90">
                                                        Compositional Mechanics
                                                    </h2>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-primary/60">
                                                            Texture & Cohesion
                                                        </span>
                                                        <div className="h-px w-8 bg-primary/20" />
                                                    </div>
                                                </div>

                                                {/* Key Verse Candidates */}
                                                <KeyVerseCandidatesCard
                                                    candidates={bookFeatures.keyVerseCandidates}
                                                    isExpanded={expandedItems.has('key-verses')}
                                                    onToggleExpansion={() => toggleItemExpansion('key-verses')}
                                                    onRefClick={(ref) => setUrl(`/${ref}`)}
                                                    thumbnailUrl={poeticIllustration}
                                                />

                                                {/* Repeated Patterns */}
                                                <RepeatedPatternsCard
                                                    patterns={bookFeatures.repeatedPatterns}
                                                    isExpanded={expandedItems.has('repeated-patterns')}
                                                    onToggleExpansion={() => toggleItemExpansion('repeated-patterns')}
                                                    thumbnailUrl={poeticIllustration}
                                                />

                                                {/* Intertextual Arcs */}
                                                <IntertextualArcsCard
                                                    arcs={bookFeatures.intertextualArcs}
                                                    isExpanded={expandedItems.has('intertextual-arcs')}
                                                    onToggleExpansion={() => toggleItemExpansion('intertextual-arcs')}
                                                    onRefClick={(ref) => setUrl(`/${ref}`)}
                                                    thumbnailUrl={poeticIllustration}
                                                />
                                            </div>

                                            {/* Section 3: Voice & Tradition */}
                                            <div className="space-y-6 pt-8">
                                                <div className="space-y-1">
                                                    <h2 className="text-2xl font-serif font-semibold tracking-tight text-foreground/90">
                                                        Voice & Tradition
                                                    </h2>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-primary/60">
                                                            Origin & Orality
                                                        </span>
                                                        <div className="h-px w-8 bg-primary/20" />
                                                    </div>
                                                </div>

                                                {/* Author's Style & Language */}
                                                <AuthorVoiceCard
                                                    author={bookFeatures.biblicalContext.author}
                                                    isExpanded={expandedItems.has('author-voice')}
                                                    onToggleExpansion={() => toggleItemExpansion('author-voice')}
                                                    thumbnailUrl={toolsIllustration}
                                                />

                                                {/* Oral Tradition */}
                                                <OralTraditionCard
                                                    oralTradition={bookFeatures.oralTradition}
                                                    isExpanded={expandedItems.has('oral-tradition')}
                                                    onToggleExpansion={() => toggleItemExpansion('oral-tradition')}
                                                    thumbnailUrl={oralIllustration}
                                                />
                                            </div>

                                            {/* Book-Level Sources */}
                                            {bookFeatures?.sources?.length > 0 && (
                                                <div className="pt-8 border-t border-border/10">
                                                    <div className="flex items-center gap-2 mb-4 px-2">
                                                        <Scroll className="h-3.5 w-3.5 text-muted-foreground/40" />
                                                        <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/40">Macro-Level Sources</span>
                                                    </div>
                                                    <SourceLinks sources={bookFeatures.sources} />
                                                </div>
                                            )}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    )}

                {/* AI Disclaimer at the bottom */}
                <div className="py-8 border-t border-border/50 flex flex-col items-center gap-4">
                    <AIDisclaimerBadge
                        model={passageFeatures?.generatedBy || (passageFeatures as any)?.source}
                        passageId={passageId}
                        contextType="literary-context"
                    />
                    <p className="text-[10px] text-muted-foreground text-center max-w-[400px]">
                        This literary analysis is generated from the BibleGlen Literary Module, utilizing Gemini 3.0 Pro to synthesize prose and poetic patterns. Always cross-reference with standard critical commentaries for technical studies.
                    </p>
                </div>

                {/* Cross-Reference Modal */}
                {
                    crossRefModal && (
                        <LiteraryFeatureCrossReferenceModal
                            featureType={crossRefModal.type}
                            featureValue={crossRefModal.value}
                            isOpen={!!crossRefModal}
                            onClose={() => setCrossRefModal(null)}
                        />
                    )
                }
            </div>
        </div>
    );
}


